import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import InstallShortcutBanner from "../components/InstallShortcutBanner";
import { useLocalization } from "../hooks/useLocalization";
import {
  BackgroundFixed,
  BgCover,
  BgVignette,
  BrandLeft,
  ContentInner,
  ContentWrap,
  DustOverlay,
  FooterOverlay,
  FooterSpacer,
  FooterWrap,
  HeaderPill,
  HeaderWrap,
  LangButton,
  LangItem,
  LangMenu,
  LangWrap,
  PoweredText,
  Shell,
  SrOnly,
  WhoscallLogo,
} from "./components";

const GlobeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
    <path
      d="M12 21c4.97 0 9-4.03 9-9s-4.03-9-9-9-9 4.03-9 9 4.03 9 9 9Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M3 12h18M12 3c2.5 2.6 3.75 5.3 3.75 9S14.5 17.4 12 21m0-18c-2.5 2.6-3.75 5.3-3.75 9S9.5 17.4 12 21"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </svg>
);

/* Layout Options (per-page) */
type LayoutOptions = {
  /** 背景圖向下位移（px）。正數＝往下看、負數＝往上看 */
  bgOffsetY?: number;
  /** Footer 是否固定在底部 */
  footerFixed?: boolean;
  /** Footer 基礎高度（行動） */
  footerHeight?: number; // mobile base (預設 38)
  /** 內容區域的 overflow-Y */
  overflowY?: "scroll" | "hidden";
  /** （可選）桌機內容最大寬度；預設 1240 */
  desktopMaxWidth?: number;
  /** （可選）平板內容最大寬度；預設 1080 */
  tabletMaxWidth?: number;
};

type Props = {
  options?: LayoutOptions;
};

export default function CityResponsiveLayout({ options }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { locale, setLocale } = useLocalization();

  const merged: Required<LayoutOptions> = {
    bgOffsetY: 0,
    footerFixed: true,
    footerHeight: 38, // base (mobile)。平板最少 48，桌機最少 56（樣式內自動提升）
    overflowY: "hidden",
    desktopMaxWidth: 1240,
    tabletMaxWidth: 1080,
    ...options,
  };

  // 切頁時回到頂端（避免沿用上一頁的 scroll 位置）
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    (document.scrollingElement || document.documentElement).scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);

  // 語系切換 UI
  const langOptions = useMemo(
    () => [
      { code: "en", label: "English" },
      { code: "zh", label: "繁體中文" },
    ],
    [],
  );

  const [open, setOpen] = useState<boolean>(false);
  const [focusIdx, setFocusIdx] = useState<number>(-1);

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const itemsRef = useRef<Array<HTMLButtonElement | null>>([]);

  const toggleOpen = useCallback(() => setOpen((o) => !o), []);
  const close = useCallback(() => {
    setOpen(false);
    setFocusIdx(-1);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") return close();
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setFocusIdx((prev) => {
          const max = langOptions.length - 1;
          return e.key === "ArrowDown"
            ? prev < max
              ? prev + 1
              : 0
            : prev > 0
              ? prev - 1
              : max;
        });
      }
      if (e.key === "Enter" && focusIdx >= 0)
        handleSelect(langOptions[focusIdx].code as "en" | "zh");
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, langOptions, focusIdx, close]);

  useEffect(() => {
    if (focusIdx >= 0) itemsRef.current[focusIdx]?.focus();
  }, [focusIdx]);

  const handleSelect = (code: "en" | "zh") => {
    setLocale(code);
    close();
    btnRef.current?.focus();
  };

  // FooterSpacer：若固定 footer，提供對應的內容底部緩衝
  const contentPadBottom = merged.footerFixed ? (merged.footerHeight ?? 38) : 0;

  return (
    <Shell $overflowY={merged.overflowY}>
      {/* 背景 */}

      <InstallShortcutBanner />

      <BackgroundFixed aria-hidden>
        <BgCover $offsetY={merged.bgOffsetY} />
        <BgVignette />
      </BackgroundFixed>
      <DustOverlay aria-hidden />

      {/* Header */}
      <HeaderWrap>
        <HeaderPill>
          <BrandLeft onClick={() => navigate("/")}>
            {/* <img
              className="brand"
              style={{ height: 38 }}
              src="/images/yunlin-logo.png"
              alt="Yunlin Logo"
            />
            <span className="pipe">|</span> */}
            <img
              className="brand"
              src="/images/foxconn-logo.svg"
              alt="Foxconn Logo"
            />
            <span className="pipe">|</span>
          </BrandLeft>

          <LangWrap ref={wrapRef}>
            <LangButton
              ref={btnRef}
              aria-haspopup="menu"
              aria-expanded={open}
              aria-controls="lang-menu"
              onClick={(e) => {
                e.preventDefault();
                toggleOpen();
              }}
            >
              <GlobeIcon />
              <SrOnly>Change language</SrOnly>
            </LangButton>

            {open && (
              <LangMenu id="lang-menu" role="menu" aria-label="Language menu">
                {langOptions.map((opt, idx) => (
                  <LangItem key={opt.code} $active={opt.code === locale}>
                    <button
                      ref={(el) => (itemsRef.current[idx] = el)}
                      role="menuitemradio"
                      aria-checked={opt.code === locale}
                      onClick={(e) => {
                        e.preventDefault();
                        handleSelect(opt.code as "en" | "zh");
                      }}
                      onMouseEnter={() => setFocusIdx(idx)}
                    >
                      {opt.label}
                    </button>
                  </LangItem>
                ))}
              </LangMenu>
            )}
          </LangWrap>
        </HeaderPill>
      </HeaderWrap>

      {/* Page Content */}
      <ContentWrap $padBottom={contentPadBottom}>
        <ContentInner
          $tabletMax={merged.tabletMaxWidth}
          $desktopMax={merged.desktopMaxWidth}
        >
          <Outlet />
        </ContentInner>
      </ContentWrap>

      {/* Footer */}
      <FooterWrap>
        {merged.footerFixed && (
          <FooterSpacer $height={merged.footerHeight ?? 38} />
        )}
        <FooterOverlay
          $fixed={merged.footerFixed}
          $height={merged.footerHeight}
        >
          <PoweredText>powered by</PoweredText>
          <WhoscallLogo src="/images/whoscall-logo-white.svg" alt="whoscall" />
        </FooterOverlay>
      </FooterWrap>
    </Shell>
  );
}
